import {useEffect, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'

import SignIn from './SignIn'
import TfaBypassDisabled from './tfa/TfaBypassDisabled'
import TfaLogIn from './tfa/TfaLogIn'
import TfaBackupLogIn from './tfa/TfaBackupLogIn'

import {connect} from '../../state/hooks/tools'
import {createResource, readEndpoint} from '../../lib/asyncMethods'
import {asyncActions} from '../../lib/asyncUtil'
import {setToken, removeToken, setTrustToken, getToken, setRefreshToken} from '../../lib/auth'
import {SIGN_IN, ROLES, SETTINGS, TFA_ENROLLMENTS} from '../../state/actionTypes'
import {findRecordByAttribute} from '../../lib/data'
import {holdsTfaBypass} from '../../lib/featureFlags'

const SignInController = props => {
  const [mode, setMode] = useState('basic')
  // the flag read below runs on its own dispatch, so the sign-in form's own loading state says
  // nothing about it and would take a second submission while it is in flight
  const [resolving, setResolving] = useState(false)
  const navigate = useNavigate()
  const [query] = useSearchParams()

  useEffect(() => {
    if (__DEV__)
      console.info('[development] Removing authentication token.')

    removeToken('authToken')
    props.clearError()
  }, [])

  const alternativeLogIn = () => {
    props.clearError()
    setMode('tfaBackup')
  }

  const goToNextRoute = () => {
    const nextRoute = query.get('nextRoute')

    if (nextRoute)
      navigate(`/${nextRoute}`)
    else
      navigate('/dashboard')
  }

  const gotToTfa = config => {
    if (config?.status === 'enabled')
      setMode('tfa')
    else
      navigate('../tfa-setup', {replace: true})
  }

  const startTfa = () => {
    props.readEnrollments('tfa-enrollments')
    props.createResource({endpoint: 'tfa-configs', attributes: {}}).then(result => gotToTfa(result.data)).catch(props.reportUnreachable)
  }

  /**
   * An exempt account has no second factor to be asked for, so the pre-auth token it was just handed
   * leads nowhere while the exemption is off. The token goes with the dead end rather than sitting
   * in storage against a sign-in that never finished.
   */
  const haltOnDisabledBypass = () => {
    removeToken('authToken')
    setMode('bypassDisabled')
  }

  /**
   * A sign-in that stopped short of a session owes either a second factor or an explanation, and the
   * account's feature flags are the only thing that tells the two apart. A bypass this account holds
   * means it does not use 2FA — an enabled one would have granted the session above, so reaching
   * here with one means it is switched off. Roles come along to say whose flag was read, since the
   * listing an admin is handed is not their own. Either read failing falls through to the second
   * factor, which is the answer for every account that has no bypass anyway.
   */
  const resolveSecondFactor = () => {
    setResolving(true)

    Promise.all([props.readSettings('settings'), props.readRoles('roles')])
      .then(([settings, roles]) => {
        if (holdsTfaBypass({settings: settings.data, roles: roles.data}))
          haltOnDisabledBypass()
        else
          startTfa()
      })
      .catch(startTfa)
      .finally(() => setResolving(false))
  }

  const handleSubmit = data => {
    const trustToken = getToken('trustToken')
    let attributes = data

    if (trustToken)
      attributes = {...attributes, trustToken}

    props.createResource({endpoint: 'auth/sign-in', attributes}).then(response => {
      if (!response.success)
        return

      setToken(response.data.authToken)

      if (response.data.refreshToken)
        setRefreshToken(response.data.refreshToken)

      if (response.data.id)
        goToNextRoute()
      else
        resolveSecondFactor()
    }).catch(props.reportUnreachable)
  }

  const tfaLogIn = data => {
    const primaryEnrollment = findRecordByAttribute({records: props.tfaEnrollments, attribute: 'category', id: 'primary'})

    props.createResource({endpoint: 'auth/tfa-sign-in', attributes: {...data, enrollmentId: primaryEnrollment.id}}).then(response => {
      if (response.success) {
        setToken(response.data.authToken)

        if (response.data.trustToken)
          setTrustToken(response.data.trustToken)

        if (response.data.refreshToken)
          setRefreshToken(response.data.refreshToken)

        goToNextRoute()
      }
    }).catch(props.reportUnreachable)
  }

  const dismissNotice = () => {
    setMode('basic')
  }

  switch(mode) {
    case 'tfa':
      return <TfaLogIn handleSubmit={tfaLogIn} fallback={alternativeLogIn} loading={props.auth.loading} error={props.auth.error} />
    case 'tfaBackup':
      return <TfaBackupLogIn handleSubmit={tfaLogIn} loading={props.auth.loading} error={props.auth.error} />
    case 'bypassDisabled':
      return <TfaBypassDisabled onDismiss={dismissNotice} />
    default:
      return <SignIn handleSubmit={handleSubmit} loading={props.auth.loading || resolving} error={props.auth.error} />
  }
}

const mapStateToProps = state => ({auth: state.auth, ...state.tfaEnrollment})

const actionCreators = dispatchs => ({
  createResource: createResource(dispatchs.auth, SIGN_IN),
  readEnrollments: readEndpoint(dispatchs.tfaEnrollment, TFA_ENROLLMENTS),
  readSettings: readEndpoint(dispatchs.metadata, SETTINGS),
  readRoles: readEndpoint(dispatchs.roles, ROLES),
  // the reducer clears the error whenever a request starts, and nothing else clears it
  clearError: () => dispatchs.auth(asyncActions(SIGN_IN).loading(false)),
  // a request that never got an answer leaves the browser's own wording behind, such as "Failed to fetch"
  reportUnreachable: () => dispatchs.auth(asyncActions(SIGN_IN).failure(true, "We couldn't reach the server. Check your connection and try again."))
})

export default connect(mapStateToProps, actionCreators)(SignInController)
